'use client';

import { createContext, useEffect, useState, type ReactNode } from 'react';

export type Density = 'compact' | 'comfortable';

export interface DensityContextValue {
  density: Density;
  setDensity: (density: Density) => void;
}

export const DensityContext = createContext<DensityContextValue | null>(null);

const STORAGE_KEY = 'heyroot-density';

function readInitial(initialDensity: Density): Density {
  if (typeof window === 'undefined') return initialDensity;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'compact' || stored === 'comfortable') return stored;
  } catch {
    // Keep the supplied preference when browser storage is unavailable.
  }
  return initialDensity;
}

export interface DensityProviderProps {
  initialDensity?: Density;
  children: ReactNode;
}

export function DensityProvider({
  initialDensity = 'comfortable',
  children,
}: DensityProviderProps) {
  const [density, setDensityInternal] = useState<Density>(() => readInitial(initialDensity));

  useEffect(() => {
    document.documentElement.dataset['density'] = density;
  }, [density]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === 'compact' || e.newValue === 'comfortable') setDensityInternal(e.newValue);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  function setDensity(next: Density) {
    setDensityInternal(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      console.warn('[heyroot/ui] failed to persist preference to localStorage:', err);
    }
  }

  return (
    <DensityContext.Provider value={{ density, setDensity }}>{children}</DensityContext.Provider>
  );
}
